/**
 * `decodeExtra` (W1-08 formats-legacy-respack; contract formats/legacy/types.ts): Phira's extra.json (shader effects
 * and background videos; formats-and-packaging.md P§5.1) -> IrExtraJson, leniently, with Phira's fatal list.
 *
 * What Phira requires (each violation fails the WHOLE chart; we repair, report 'changed' and list the reason in
 * `phiraFatal`): strict UTF-8 without a BOM, valid JSON, a root object with `bpm` (a number or a list of
 * {time, bpm}), beat triples of JSON integers, effects with `start`, `end`, a known built-in shader or a
 * "/path" shader, variables of an untagged shape tried Float -> Vec2 -> Color (a colour is four INTEGERS 0..255;
 * `[0, 0, 0, 1.0]` matches nothing), keyframe lists that are not empty, a required integer `easingType`, video
 * `scale` one of cropCenter / inside / fit, video `time` / `scale` absent or valid (serde defaults only absent
 * fields, so an explicit null is refused), and no duplicated field.
 *
 * Times: the file's own BPM list (f64 BPMs, like Phira reads them) is independent of the chart's. When it equals
 * the chart's BPMList (same f64 beats and BPMs, in order) every triple is kept as written; otherwise each time is
 * converted through seconds into a chart beat (`convertedBpm`, reported; 'changed' when a conversion is not
 * exact). The file's seconds come from Phira's literal BpmList walk (pgr/legacyIr.ts `phiraBpmSeconds`), which
 * Phira applies to the list as written, unsorted ones included. A converted time beyond the legacy beat range
 * (or at no finite time) is clamped to it like the PGR / PEC importers clamp theirs (`timeOutOfRange`, 'changed'),
 * so no input makes the decoder throw. The result always carries the chart's BPMList (encodeExtra writes it).
 *
 * Kept as Phira treats them: `null` variables (an empty anim: the uniform is 0 for effects), unknown keys of the
 * root / effects / videos (passthrough), keyframes out of time order (flagged: the engine plays them forward like
 * Phira) and effects that are never active (flagged).
 */

import { BUILTIN_SHADER_NAMES } from '../../core/model/schema';
import { Beats, I32_MAX, I32_MIN, U32_MAX } from '../../core/time/beat';
import { createTempoMap } from '../../core/time/tempo';
import type { Beat, TempoMap, Triple } from '../../core/time/types';
import type { IrEffect, IrExtraJson, IrExtraKeyframe, IrVarValue, IrVideo } from '../../core/ir/types';
import { mapNonEmpty } from '../../core/util/nonEmpty';
import type { Json, Passthrough, RGBA8, Vec2 } from '../../core/util/types';
import { nodeToJson, parseJsonLossless } from '../json/lexer';
import type { JsonNode } from '../json/types';
import type { DecodeExtraFn, ExtraDecodeResult } from '../legacy/types';
import {
  beatOfF64,
  canonicalF32,
  LEGACY_BEAT_MAX,
  LEGACY_BEAT_MIN,
  LegacyReport,
  phiraBpmSeconds,
} from '../pgr/legacyIr';

const fr = Math.fround;

type ObjNode = Extract<JsonNode, { readonly k: 'obj' }>;
type NumNode = Extract<JsonNode, { readonly k: 'num' }>;
type VarKind = 'float' | 'vec2' | 'color';

const ROOT_KEYS = ['bpm', 'effects', 'videos'] as const;
const EFFECT_KEYS = ['start', 'end', 'shader', 'vars', 'global'] as const;
const VIDEO_KEYS = ['path', 'time', 'scale', 'alpha', 'dim', 'attach'] as const;
const KEYFRAME_KEYS = ['startTime', 'endTime', 'start', 'end', 'easingType', 'easingLeft', 'easingRight'] as const;
const VIDEO_SCALES = ['cropCenter', 'inside', 'fit'] as const;

/**
 * Package path of a custom shader: Phira strips exactly one leading '/' ("/shaders/a.glsl" -> "shaders/a.glsl");
 * null for built-in names (and for "/" alone, which names no file).
 */
export function customShaderPath(shader: string): string | null {
  return shader.startsWith('/') && shader.length > 1 ? shader.slice(1) : null;
}

/** Is `name` one of Phira's ten built-in shaders? */
export function isBuiltinShader(name: string): boolean {
  return (BUILTIN_SHADER_NAMES as readonly string[]).includes(name);
}

function join(path: string, key: string | number): string {
  if (typeof key === 'number') return `${path}[${String(key)}]`;
  return path === '' ? key : `${path}.${key}`;
}

function isInt(n: NumNode): boolean {
  return n.int;
}

/**
 * Equal BPM lists in Phira's terms: both become a BpmList of f64 (beat, bpm) pairs in list order (the chart's
 * RPE `bpm` and extra.json's `bpm` are both read as f64), so they convert every time alike exactly when every pair
 * is equal in f64.
 */
function sameBpm(
  a: ReadonlyArray<{ readonly bpm: number; readonly startTime: Triple }>,
  b: ReadonlyArray<{ readonly bpm: number; readonly startTime: Triple }>,
): boolean {
  return (
    a.length === b.length &&
    a.every((p, i) => {
      const q = b[i];
      return q !== undefined && p.bpm === q.bpm && Beats.tripleToF64(p.startTime) === Beats.tripleToF64(q.startTime);
    })
  );
}

/** The f64 beat of a triple node as written (no report), or null when it is not three numbers. */
function rawBeats(node: JsonNode | undefined): number | null {
  if (node?.k !== 'arr' || node.items.length !== 3) return null;
  const [a, b, c] = node.items;
  if (a?.k !== 'num' || b?.k !== 'num' || c?.k !== 'num') return null;
  return a.v + b.v / c.v;
}

/** The written startTime beat of the first keyframe of a keyframe-list node, or null. */
function firstStartBeats(node: JsonNode): number | null {
  if (node.k !== 'arr') return null;
  const first = node.items[0];
  if (first?.k !== 'obj') return null;
  return rawBeats(first.entries.find(([k]) => k === 'startTime')?.[1]);
}

class Decoder {
  readonly report = new LegacyReport();
  readonly fatal: string[] = [];
  /** Converts a file triple into a chart triple (identity when the BPM lists match). */
  convert: (t: Triple) => Triple = (t) => t;
  /** Seconds of a file beat through the file's own BPM list (set when the lists differ). */
  fileSec: ((beats: number) => number) | null = null;
  converted = 0;
  inexact = 0;
  /** Converted times beyond the legacy beat range (or at no finite time), clamped. */
  outOfRange = 0;

  phiraFatal(key: string, path: string, params?: Readonly<Record<string, string | number>>): void {
    if (!this.fatal.includes(key)) this.fatal.push(key);
    this.report.fatal(key, params, path === '' ? {} : { path });
  }

  /** Object members; Phira refuses a duplicated KNOWN field (serde "duplicate field"). */
  members(
    node: ObjNode,
    known: readonly string[],
    path: string,
  ): { get(k: string): JsonNode | undefined; extra: Passthrough } {
    const map = new Map<string, JsonNode>();
    const extra = new Map<string, Json>();
    for (const [k, v] of node.entries) {
      if (known.includes(k)) {
        if (map.has(k)) this.phiraFatal('extra.report.duplicateKey', join(path, k), { field: k });
        map.set(k, v);
      } else {
        extra.delete(k);
        extra.set(k, nodeToJson(v));
      }
    }
    return { get: (k) => map.get(k), extra: [...extra.entries()] };
  }

  wrongType(path: string, field: string): void {
    this.phiraFatal('extra.report.wrongType', path, { field });
  }

  /** A beat triple of JSON integers: a i32, b and c u32; null when unusable (c = 0 gives NaN times in Phira). */
  triple(node: JsonNode | undefined, path: string, field: string): Triple | null {
    if (node === undefined) {
      this.phiraFatal('extra.report.missingField', path, { field });
      return null;
    }
    if (node.k !== 'arr' || node.items.length !== 3 || node.items.some((n) => n.k !== 'num')) {
      this.wrongType(path, field);
      return null;
    }
    const nums = node.items as readonly NumNode[];
    if (nums.some((n) => !isInt(n))) this.phiraFatal('extra.report.floatInTriple', path, { field });
    const [a0, b0, c0] = nums.map((n) => Math.round(n.v));
    let a = a0 ?? 0;
    let b = b0 ?? 0;
    let c = c0 ?? 1;
    if (a < I32_MIN || a > I32_MAX || b < 0 || b > U32_MAX || c < 0 || c > U32_MAX) {
      this.phiraFatal('extra.report.tripleRange', path, { field });
      a = Math.min(Math.max(a, I32_MIN), I32_MAX);
      b = Math.min(Math.max(b, 0), U32_MAX);
      c = Math.min(Math.max(c, 0), U32_MAX);
    }
    if (c === 0) {
      this.report.add('changed', 'extra.report.tripleZeroDen', { field }, { path });
      return null;
    }
    return this.convert([a, b, c]);
  }

  /** A number Phira reads as f64 (BPM values): kept exactly; null when missing, not a number or not finite. */
  f64(node: JsonNode | undefined, path: string, field: string): number | null {
    if (node === undefined) {
      this.phiraFatal('extra.report.missingField', path, { field });
      return null;
    }
    if (node.k !== 'num') {
      this.wrongType(path, field);
      return null;
    }
    if (!Number.isFinite(node.v)) {
      this.phiraFatal('extra.report.outOfRange', path, { field });
      return null;
    }
    return node.v;
  }

  number(node: JsonNode | undefined, path: string, field: string): number | null {
    if (node === undefined) {
      this.phiraFatal('extra.report.missingField', path, { field });
      return null;
    }
    if (node.k !== 'num') {
      this.wrongType(path, field);
      return null;
    }
    if (!Number.isFinite(fr(node.v))) {
      this.phiraFatal('extra.report.outOfRange', path, { field });
      return null;
    }
    // Phira reads these fields as f32: keep the value exactly as encodeExtra writes it back.
    return canonicalF32(node.v);
  }

  /** Four integers 0..255; floats / out-of-range values (which Phira rejects) are converted and reported. */
  color(node: Extract<JsonNode, { readonly k: 'arr' }>, path: string, field: string): RGBA8 {
    const nums = node.items as readonly NumNode[];
    const ok = nums.every((n) => isInt(n) && n.v >= 0 && n.v <= 255);
    if (ok) return [nums[0]?.v ?? 0, nums[1]?.v ?? 0, nums[2]?.v ?? 0, nums[3]?.v ?? 0];
    this.phiraFatal('extra.report.colourNotBytes', path, { field });
    const unit = nums.every((n) => n.v >= 0 && n.v <= 1) && nums.some((n) => !isInt(n));
    const c = (v: number): number => Math.min(255, Math.max(0, Math.round(unit ? v * 255 : v)));
    return [c(nums[0]?.v ?? 0), c(nums[1]?.v ?? 0), c(nums[2]?.v ?? 0), c(nums[3]?.v ?? 0)];
  }

  /** A fixed value of the given kind, or null when it does not have that shape. */
  fixed(node: JsonNode, kind: VarKind, path: string, field: string): number | Vec2 | RGBA8 | null {
    if (kind === 'float') return node.k === 'num' ? this.number(node, path, field) : null;
    if (node.k !== 'arr' || node.items.some((n) => n.k !== 'num')) return null;
    const nums = node.items as readonly NumNode[];
    if (kind === 'vec2') {
      if (nums.length !== 2) return null;
      const x = this.number(nums[0], path, field);
      const y = this.number(nums[1], path, field);
      return x === null || y === null ? null : [x, y];
    }
    return nums.length === 4 ? this.color(node, path, field) : null;
  }

  keyframe<V extends number | Vec2 | RGBA8>(
    node: JsonNode,
    kind: VarKind,
    path: string,
    field: string,
  ): IrExtraKeyframe<V> | null {
    if (node.k !== 'obj') {
      this.phiraFatal('extra.report.varShape', path, { field });
      return null;
    }
    const m = this.members(node, KEYFRAME_KEYS, path);
    for (const [k] of m.extra)
      this.report.add('normalized', 'extra.report.keyframeKeyDropped', { field: k }, { path: join(path, k) });
    const startTime = this.triple(m.get('startTime'), join(path, 'startTime'), 'startTime');
    const endTime = this.triple(m.get('endTime'), join(path, 'endTime'), 'endTime');
    const startNode = m.get('start');
    const endNode = m.get('end');
    if (startNode === undefined || endNode === undefined) {
      this.phiraFatal('extra.report.missingField', path, { field: startNode === undefined ? 'start' : 'end' });
      return null;
    }
    const start = this.fixed(startNode, kind, join(path, 'start'), field);
    const end = this.fixed(endNode, kind, join(path, 'end'), field);
    if (start === null || end === null) {
      this.phiraFatal('extra.report.varShape', path, { field });
      return null;
    }
    let easingType = 1;
    const et = m.get('easingType');
    if (et === undefined)
      this.phiraFatal('extra.report.missingField', join(path, 'easingType'), { field: 'easingType' });
    else if (et.k !== 'num') this.wrongType(join(path, 'easingType'), 'easingType');
    else {
      if (!isInt(et) || et.v < I32_MIN || et.v > I32_MAX) {
        this.phiraFatal('extra.report.floatInInt', join(path, 'easingType'), { field: 'easingType' });
      }
      easingType = Math.min(Math.max(Math.round(et.v), I32_MIN), I32_MAX);
    }
    if (startTime === null || endTime === null) return null;
    const kf: { -readonly [K in keyof IrExtraKeyframe<V>]: IrExtraKeyframe<V>[K] } = {
      startTime,
      endTime,
      start: start as V,
      end: end as V,
      easingType,
      srcKey: null,
    };
    for (const side of ['easingLeft', 'easingRight'] as const) {
      const n = m.get(side);
      if (n === undefined) continue;
      const v = this.number(n, join(path, side), side);
      if (v !== null) kf[side] = v;
    }
    return kf;
  }

  /**
   * An AnimValue: null | fixed | non-empty keyframe list. `floatOnly` for video alpha / dim. Returns undefined when
   * the value matches no shape (dropped; Phira refuses the file).
   */
  variable(node: JsonNode, path: string, field: string, floatOnly: boolean): IrVarValue | undefined {
    if (node.k === 'null') return null;
    if (node.k === 'num') return this.number(node, path, field) ?? undefined;
    if (node.k !== 'arr') {
      this.phiraFatal('extra.report.varShape', path, { field });
      return undefined;
    }
    if (node.items.length === 0) {
      this.phiraFatal('extra.report.emptyKeyframes', path, { field });
      return undefined;
    }
    if (node.items.every((n) => n.k === 'num')) {
      const fixed = floatOnly
        ? null
        : (this.fixed(node, node.items.length === 2 ? 'vec2' : 'color', path, field) ?? null);
      if (fixed === null) this.phiraFatal('extra.report.varShape', path, { field });
      return fixed ?? undefined;
    }
    if (!node.items.every((n) => n.k === 'obj')) {
      this.phiraFatal('extra.report.varShape', path, { field });
      return undefined;
    }
    // Keyframes: the first keyframe's `start` decides the shape (Float -> Vec2 -> Color).
    const first = node.items[0];
    const startOfFirst = first?.k === 'obj' ? first.entries.find(([k]) => k === 'start')?.[1] : undefined;
    let kind: VarKind = 'float';
    if (startOfFirst?.k === 'arr') kind = startOfFirst.items.length === 2 ? 'vec2' : 'color';
    if (floatOnly && kind !== 'float') {
      this.phiraFatal('extra.report.varShape', path, { field });
      return undefined;
    }
    const kfs: IrExtraKeyframe<number | Vec2 | RGBA8>[] = [];
    node.items.forEach((item, i) => {
      const kf = this.keyframe(item, kind, join(path, i), field);
      if (kf !== null) kfs.push(kf);
    });
    if (kfs.length === 0) return undefined;
    // Phira's keyframes are (start, end) of every item in list order; out of time order they play forward
    // (the engine reproduces that) but a backward seek in Phira can show other values.
    const times = kfs.flatMap((k) => [Beats.tripleToF64(k.startTime), Beats.tripleToF64(k.endTime)]);
    if (times.some((t, i) => i > 0 && t < (times[i - 1] ?? t))) {
      this.report.add('flagged', 'extra.report.keyframesUnsorted', { field }, { path });
    }
    return kfs as unknown as IrVarValue;
  }

  effect(node: JsonNode, path: string): IrEffect | null {
    if (node.k !== 'obj') {
      this.wrongType(path, 'effect');
      return null;
    }
    const m = this.members(node, EFFECT_KEYS, path);
    const start = this.triple(m.get('start'), join(path, 'start'), 'start');
    const end = this.triple(m.get('end'), join(path, 'end'), 'end');
    const shaderNode = m.get('shader');
    let shader: string | null = null;
    if (shaderNode === undefined)
      this.phiraFatal('extra.report.missingField', join(path, 'shader'), { field: 'shader' });
    else if (shaderNode.k !== 'str') this.wrongType(join(path, 'shader'), 'shader');
    else if (shaderNode.v.startsWith('/')) {
      if (customShaderPath(shaderNode.v) === null) {
        this.phiraFatal('extra.report.shaderPath', join(path, 'shader'), { name: shaderNode.v });
      } else shader = shaderNode.v;
    } else if (isBuiltinShader(shaderNode.v)) shader = shaderNode.v;
    else this.phiraFatal('extra.report.unknownShader', join(path, 'shader'), { name: shaderNode.v });
    const vars: (readonly [string, IrVarValue])[] = [];
    const varsNode = m.get('vars');
    if (varsNode !== undefined && varsNode.k !== 'null') {
      if (varsNode.k !== 'obj') this.wrongType(join(path, 'vars'), 'vars');
      else {
        for (const [name, value] of varsNode.entries) {
          const v = this.variable(value, join(join(path, 'vars'), name), name, false);
          if (v === undefined) continue;
          const at = vars.findIndex(([n]) => n === name);
          if (at >= 0) {
            this.report.add('normalized', 'extra.report.duplicateVar', { field: name }, { path: join(path, 'vars') });
            vars[at] = [name, v];
          } else vars.push([name, v]);
        }
      }
    } else if (varsNode?.k === 'null') this.wrongType(join(path, 'vars'), 'vars');
    let global = false;
    const g = m.get('global');
    if (g !== undefined) {
      if (g.k === 'bool') global = g.v;
      else this.wrongType(join(path, 'global'), 'global');
    }
    if (start === null || end === null || shader === null) return null;
    if (!(Beats.tripleToF64(start) < Beats.tripleToF64(end))) {
      this.report.add('flagged', 'extra.report.effectNeverActive', undefined, { path });
    }
    return { start, end, shader, vars, global, extra: m.extra, srcEffect: null };
  }

  video(node: JsonNode, path: string): IrVideo | null {
    if (node.k !== 'obj') {
      this.wrongType(path, 'video');
      return null;
    }
    const m = this.members(node, VIDEO_KEYS, path);
    const pathNode = m.get('path');
    let file: string | null = null;
    if (pathNode === undefined) this.phiraFatal('extra.report.missingField', join(path, 'path'), { field: 'path' });
    else if (pathNode.k !== 'str') this.wrongType(join(path, 'path'), 'path');
    else file = pathNode.v;
    // `time` and `scale` only default when ABSENT: Phira refuses an explicit null for them.
    const timeNode = m.get('time');
    let time: Triple | null;
    if (timeNode === undefined) time = this.convert([0, 0, 1]);
    else if (timeNode.k === 'null') {
      this.wrongType(join(path, 'time'), 'time');
      time = this.convert([0, 0, 1]);
    } else time = this.triple(timeNode, join(path, 'time'), 'time');
    let scale: IrVideo['scale'] = 'cropCenter';
    const scaleNode = m.get('scale');
    if (scaleNode?.k === 'null') this.wrongType(join(path, 'scale'), 'scale');
    else if (scaleNode !== undefined) {
      const known = scaleNode.k === 'str' ? VIDEO_SCALES.find((s) => s === scaleNode.v) : undefined;
      if (known === undefined)
        this.phiraFatal('extra.report.videoScale', join(path, 'scale'), {
          value: scaleNode.k === 'str' ? scaleNode.v : '',
        });
      else scale = known;
    }
    const video: { -readonly [K in keyof IrVideo]: IrVideo[K] } = {
      path: file ?? '',
      time: time ?? [0, 0, 1],
      scale,
      extra: m.extra,
      srcVideo: null,
    };
    for (const which of ['alpha', 'dim'] as const) {
      const n = m.get(which);
      if (n === undefined) continue;
      const v = this.variable(n, join(path, which), which, true);
      if (v === undefined) continue;
      video[which] = v as Exclude<IrVideo['alpha'], undefined>;
      // Phira prepends (0 s, default, hold) unless the first item starts at beat 0 of the FILE's BPM list; the
      // engine decides by seconds (core/engine/effects/extraAnim.ts). They disagree only when that beat 0 lies
      // after 0 s (a file BPM list starting before beat 0): Phira extrapolates the first item there, the chart
      // (and its export) shows the default.
      const fileSec = this.fileSec;
      if (fileSec !== null && firstStartBeats(n) === 0 && fileSec(0) > 0) {
        this.report.add('changed', 'extra.report.videoLeadIn', { field: which }, { path: join(path, which) });
      }
    }
    const attach = m.get('attach');
    if (attach !== undefined && attach.k !== 'null') {
      const lineNode = attach.k === 'obj' ? attach.entries.find(([k]) => k === 'line')?.[1] : undefined;
      if (lineNode?.k === 'num' && lineNode.int && lineNode.v >= 0 && Number.isSafeInteger(lineNode.v)) {
        video.attach = { line: lineNode.v };
      } else this.wrongType(join(path, 'attach'), 'attach');
    }
    if (file === null || time === null) return null;
    return video;
  }
}

function emptyExtra(chartBpm: IrExtraJson['bpm']): IrExtraJson {
  return { bpm: chartBpm, effects: [], videos: [], rootExtra: [] };
}

export const decodeExtra: DecodeExtraFn = (bytes, chartBpmList): ExtraDecodeResult => {
  const d = new Decoder();
  const chartBpm: IrExtraJson['bpm'] = mapNonEmpty(chartBpmList, (b) => ({ time: b.startTime, bpm: b.bpm }));
  const result = (extra: IrExtraJson, convertedBpm: boolean): ExtraDecodeResult => ({
    extra,
    convertedBpm,
    report: d.report.entries(),
    phiraFatal: d.fatal,
  });

  let text: string;
  try {
    text = new TextDecoder('utf-8', { fatal: true, ignoreBOM: true }).decode(bytes);
  } catch {
    d.phiraFatal('extra.report.invalidUtf8', '');
    text = new TextDecoder('utf-8', { ignoreBOM: true }).decode(bytes);
  }
  const parsed = parseJsonLossless(text);
  if (!parsed.ok) {
    d.phiraFatal('extra.report.syntax', '', { line: parsed.error.line, col: parsed.error.col });
    return result(emptyExtra(chartBpm), false);
  }
  if (parsed.leading.bom) d.phiraFatal('extra.report.bom', '');
  const root = parsed.node;
  if (root.k !== 'obj') {
    d.phiraFatal('extra.report.notObject', '');
    return result(emptyExtra(chartBpm), false);
  }
  const m = d.members(root, ROOT_KEYS, '');

  // BPM list of the file.
  let fileBpm: { bpm: number; startTime: Triple }[] | null = null;
  const bpmNode = m.get('bpm');
  if (bpmNode === undefined) d.phiraFatal('extra.report.missingBpm', 'bpm');
  else if (bpmNode.k === 'num') {
    const b = d.f64(bpmNode, 'bpm', 'bpm');
    if (b !== null) fileBpm = [{ bpm: b, startTime: [0, 0, 1] }];
  } else if (bpmNode.k === 'arr' && bpmNode.items.length > 0) {
    const list: { bpm: number; startTime: Triple }[] = [];
    bpmNode.items.forEach((item, i) => {
      const p = join('bpm', i);
      if (item.k !== 'obj') return;
      const time = item.entries.find(([k]) => k === 'time')?.[1];
      const bpm = item.entries.find(([k]) => k === 'bpm')?.[1];
      const t = d.triple(time, join(p, 'time'), 'time');
      const b = d.f64(bpm, join(p, 'bpm'), 'bpm');
      if (t !== null && b !== null) list.push({ bpm: b, startTime: t });
    });
    fileBpm = list.length === bpmNode.items.length ? list : null;
    if (fileBpm === null) d.wrongType('bpm', 'bpm');
  } else d.wrongType('bpm', 'bpm');
  if (fileBpm !== null && fileBpm.some((p) => !(p.bpm > 0) || !Number.isFinite(60 / p.bpm))) {
    d.report.add('changed', 'extra.report.invalidBpm', undefined, { path: 'bpm' });
    fileBpm = null;
  }
  const chartList = chartBpmList.map((b) => ({ bpm: b.bpm, startTime: b.startTime }));
  const convertedBpm = fileBpm !== null && !sameBpm(fileBpm, chartList);
  if (fileBpm !== null && convertedBpm) {
    // The file's list exactly as Phira walks it (in list order, even unsorted); the chart's is sorted.
    const fileSec = phiraBpmSeconds(fileBpm.map((p) => ({ beat: Beats.tripleToF64(p.startTime), bpm: p.bpm })));
    const chartTempo: TempoMap = createTempoMap(chartList);
    d.fileSec = fileSec;
    d.convert = (t) => {
      d.converted++;
      const sec = fileSec(Beats.tripleToF64(t));
      // A time far away on one list can lie beyond the i32 beat range (or at an infinite time) on the other:
      // clamped to the legacy beat range like the PGR / PEC importers do (NaN -> beat 0), and reported.
      const x = Number.isFinite(sec) ? chartTempo.beatF64At(sec) : sec;
      if (!Number.isFinite(x) || x < LEGACY_BEAT_MIN || x > LEGACY_BEAT_MAX) {
        d.outOfRange++;
        return Beats.toTriple(beatOfF64(x).beat);
      }
      let beat: Beat;
      try {
        beat = chartTempo.beatAt(sec);
      } catch {
        // Only for a degenerate chart tempo (e.g. an infinite seconds-per-beat): the plain beat value.
        beat = beatOfF64(x).beat;
      }
      if (!(Math.abs(chartTempo.secAt(beat) - sec) <= 1e-9 * Math.max(1, Math.abs(sec)))) d.inexact++;
      return Beats.toTriple(beat);
    };
  }

  const list = (key: 'effects' | 'videos'): readonly JsonNode[] => {
    const n = m.get(key);
    if (n === undefined) return [];
    if (n.k !== 'arr') {
      d.wrongType(key, key);
      return [];
    }
    return n.items;
  };
  const effects: IrEffect[] = [];
  list('effects').forEach((n, i) => {
    const e = d.effect(n, join('effects', i));
    if (e !== null) effects.push(e);
  });
  const videos: IrVideo[] = [];
  list('videos').forEach((n, i) => {
    const v = d.video(n, join('videos', i));
    if (v !== null) videos.push(v);
  });

  if (convertedBpm) {
    d.report.add('normalized', 'extra.report.bpmConverted', { count: d.converted });
    if (d.inexact > 0) d.report.add('changed', 'extra.report.bpmInexact', undefined, {}, d.inexact);
    if (d.outOfRange > 0) d.report.add('changed', 'extra.report.timeOutOfRange', undefined, {}, d.outOfRange);
  }
  const extra: IrExtraJson = {
    bpm: chartBpm,
    effects,
    videos,
    rootExtra: m.extra,
  };
  return result(extra, convertedBpm);
};
